import React, { useState, useEffect, useRef } from "react";
import { useStore } from "@nanostores/react";
import { Transition } from "@headlessui/react";
import Container from "./container";
import {
    $updatedConsent,
    $gdprMarketing,
    $gdprPreferences,
    $gdprStatistics,
    $gdprShown,
    $couponHeight
} from "../../utils/store";
import { __t } from "../../utils/translate";
import { log } from "../../utils/api";
import { gtag } from "../../utils/utils";

function truncateText(text, limit) {
    if (text.length <= limit) return text;

    let truncated = text.slice(0, limit);

    if (text[limit] !== ' ') {
        const lastSpaceIndex = truncated.lastIndexOf(' ');
        truncated = truncated.slice(0, lastSpaceIndex);
    }

    return truncated + '...';
}

const Cookies: React.FC = () => {
    const [showSettings, setShowSettings] = useState(false);
    const [gdprShown, setShownGdpr] = useState(true);

    const gdpr = useStore($gdprShown);
    const marketing = useStore($gdprMarketing);
    const preferences = useStore($gdprPreferences);
    const statistics = useStore($gdprStatistics);
    const couponHeight = useStore($couponHeight);

    const style = {
        transform: `translate(${0}px, -${couponHeight}px)`,
    };

    const [openPanel, setOpenPanel] = useState<string | null>(null);

    const updatedConsent = useStore($updatedConsent);

    const newRef = useRef(null);

    useEffect(() => {
        if (showSettings) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [showSettings]);

    const handleOutsideClick = (e) => {
        if (e.target === newRef.current) {
            setShowSettings(false);
        }
    }

    const updateConsent = () => {
        if (!updatedConsent) {
            $updatedConsent.set(true);

            gtag('consent', 'update', {
                'ad_storage': marketing ? 'granted' : 'denied',
                'ad_personalization': marketing ? 'granted' : 'denied',
                'ad_user_data': marketing ? 'granted' : 'denied',
                'analytics_storage': statistics ? 'granted' : 'denied',
                'functionality_storage': preferences ? 'granted' : 'denied',
                'personalization_storage': preferences ? 'granted' : 'denied',
                'security_storage': 'granted'
            });
            if (marketing) {
                gtag('set', { ads_data_redaction: false });
            }
        }
    }

    useEffect(() => {
        if (gdpr) {
            setShownGdpr(true);
            updateConsent();
        } else {
            setShownGdpr(false);
        }
    }, [gdpr]);

    const handleAccept = () => {
        if (typeof window !== 'undefined') {
            $gdprShown.set(true);
            setShownGdpr(true);
            updateConsent();
            setShowSettings(false);
            log('click', 'cookie', {
                'ad_storage': marketing ? 'granted' : 'denied',
                'ad_personalization': marketing ? 'granted' : 'denied',
                'ad_user_data': marketing ? 'granted' : 'denied',
                'analytics_storage': statistics ? 'granted' : 'denied',
                'functionality_storage': preferences ? 'granted' : 'denied',
                'personalization_storage': preferences ? 'granted' : 'denied',
                'security_storage': 'granted'
            });
        } else {
            log('error', 'cookie', { message: "handleAccept: window is undefined" });
        }
    };

    const handleSettingsClick = () => {
        setShowSettings(prevState => {
            if (prevState) {
                log('click', 'cookie', { message: "close settings" });
            } else {
                log('click', 'cookie', { message: "open settings" });
            }
            return !prevState
        });
    };

    const toggleOpenPanel = (label: string) => {
        if (openPanel === label) {
            setOpenPanel(null);
        } else {
            setOpenPanel(label);
        }
    };

    const privacy = ["This website uses cookies to improve your experience while you navigate through the website. Out of these cookies, the cookies that are categorized as necessary are stored on your browser as they are essential for the working of basic functionalities of the website. We also use third-party cookies that help us analyze and understand how you use this website. These cookies will be stored in your browser only with your consent. You also have the option to opt-out of these cookies. But opting out of some of these cookies may have an effect on your browsing experience.",
        'NextRoll, Inc. ("NextRoll") and our advertising partners use cookies and similar technologies on this site and around the web to collect and use personal data (e.g., your IP address). If you consent, the cookies, device identifiers, or other information can be stored or accessed on your device for the purposes described below. You can click "Allow All" or "Decline All" or click Settings above to customize your consent.',
        'NextRoll and our advertising partners process personal data to: ● Store and/or access information on a device; ● Create a personalized content profile; ● Select personalised content; ● Personalized ads, ad measurement and audience insights; ● Product development. For some of the purposes above, our advertising partners: ● Use precise geolocation data. Some of our partners rely on their legitimate business interests to process personal data. View our advertising partners to see the purposes they believe they have a legitimate interest for and how you can object to such processing.',
        "Your changes on this site will be applied across the internet on any digital property you visit using NextRoll's technology. If you select Decline All, you will still be able to view content on this site and you will still receive advertising, but the advertising will not be tailored for you. You may change your setting whenever you see"];

    const [showAllPrivacy, setShowAllPrivacy] = useState(false);

    const translatedPrivacy = privacy.map(__t);
    const privacyText = translatedPrivacy.join('<div class=""></div>');
    const limit = 250;
    const shortPrivacyText = truncateText(translatedPrivacy.join(''), limit);

    const toggleShowAllPrivacy = () => {
        setShowAllPrivacy(prevShowAllPrivacy => !prevShowAllPrivacy);
    }

    return (
        <>
            <Transition
                appear={true}
                show={showSettings}
                className="flex-[0_0_100%] fixed inset-0 z-[90] flex items-center justify-center"
                enter="transition-all ease duration-500"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-all ease duration-500"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div ref={newRef} onClick={handleOutsideClick} className="fixed inset-0 z-[90] flex items-end justify-center bg-[#041829] bg-opacity-50">
                    <div className="relative bg-white w-full py-7 md:py-8 lg:py-10 flex flex-col items-center overflow-x-visible">
                        <div className="flex flex-col gap-3 h-full w-full justify-between items-start overflow-visible max-w-[1032px] mx-auto box-border">
                            <div className="flex flex-col gap-4 md:gap-10 w-full h-fit max-h-[70vh] sm:max-h-[77vh] overflow-y-auto">
                                <div className="px-4 md:px-5 flex flex-col gap-2 lg:gap-4 items-start">
                                    <h3 className="text-h3 pb-2 lg:text-[32px] lg:leading-10 lg:tracking-[-0.64px] text-[#2C2D33] font-medium">{__t("Privacy Overview")}</h3>
                                    <p className="text-[#6B6C70] text-[15px] leading-[21px] lg:text-[18px] lg:leading-[26px]">
                                        {showAllPrivacy
                                            ? (<div className="flex flex-col gap-1" dangerouslySetInnerHTML={{ __html: privacyText }} />)
                                            : shortPrivacyText}
                                    </p>
                                    {privacyText.length > 300 && (
                                        <button onClick={toggleShowAllPrivacy} className="text-base-16 font-semibold lg:text-[18px] lg:leading-[26px] text-[#2C2D33] hover:text-button-accent-color cursor-pointer">
                                            {showAllPrivacy ? __t('Show less') : __t('Show more')}
                                        </button>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2 md:gap-3 lg:gap-4 w-full p-4 md:p-5 ">
                                    <Setting
                                        label="Necessary"
                                        onChange={() => { }}
                                        description={"Necessary cookies are absolutely essential for the website to function properly. This category only includes cookies that ensures basic functionalities and security features of the website. These cookies do not store any personal information."}
                                        isOpen={openPanel === 'Necessary'}
                                        handleOpen={() => toggleOpenPanel('Necessary')}
                                        fixed={true}
                                    />
                                    <Setting
                                        label="Preferences"
                                        value={preferences}
                                        onChange={() => { $gdprPreferences.set(!preferences) }}
                                        description={"Preference cookies enable a website to remember information that changes the way the website behaves or looks, like your preferred language or the region that you are in."}
                                        isOpen={openPanel === 'Preferences'}
                                        handleOpen={() => toggleOpenPanel('Preferences')}
                                    />
                                    <Setting
                                        label="Statistics"
                                        value={statistics}
                                        onChange={() => { $gdprStatistics.set(!statistics) }}
                                        description={"Statistic cookies help website owners to understand how visitors interact with websites by collecting and reporting information anonymously."}
                                        isOpen={openPanel === 'Statistics'}
                                        handleOpen={() => toggleOpenPanel('Statistics')}
                                    />
                                    <Setting
                                        label="Marketing"
                                        value={marketing}
                                        onChange={() => { $gdprMarketing.set(!marketing) }}
                                        description={"Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers."}
                                        isOpen={openPanel === 'Marketing'}
                                        handleOpen={() => toggleOpenPanel('Marketing')}
                                    />
                                </div>
                            </div>
                            <div className="px-4 md:px-5 pt-4 md:pt-10 flex-shrink-0 flex flex-col md:flex-row justify-center gap-3 md:gap-6 w-full whitespace-nowrap">
                                <button onClick={handleSettingsClick} className="btn md:max-w-[344px] border border-[#808080] text-[#222] hover:text-dark-blu">{__t('Cancel')}</button>
                                <button onClick={handleAccept} className="btn md:max-w-[344px] bg-button-accent-color text-white hover:bg-dark-blu">{__t('Save and accept')}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
            <Transition
                appear={true}
                show={!showSettings}
                className={`flex-[0_0_100%] fixed inset-0 flex items-center justify-center ${!gdprShown ? "bg-[#041829] bg-opacity-50 z-[90]" : "-z-20"}`}
                enter="transition-all ease duration-500"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-all ease duration-500"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div style={style} className={`${!gdprShown ? 'opacity-100 bottom-0 z-20' : 'opacity-0 bottom-[-200px] -z-20'} transition-all ease-in-out duration-100 fixed w-full bg-white xxl:max-w-[2170px] xxl:mx-auto`}>
                    <Container className="!max-w-max !px-4 md:px-5">
                        <div className="max-w-[992px] mx-auto flex gap-4 md:gap-5 lg:gap-6 flex-col justify-between items-center h-full text-[#2C2D33] py-7 md:py-8 lg:py-10">
                            <p className="text-h3 md:text-[32px] md:leading-10 md:tracking-[-0.64px] font-medium">{__t("We value your privacy")}</p>
                            <p className="pb-4 md:pb-5 lg:pb-9 text-base-16 leading-[21px] md:leading-[26px] md:text-[18px] text-center text-[#6B6C70]">{__t('We use cookies on our website to give you the most relevant experience by remembering your preferences and repeat visits. By clicking “Accept”, you consent to the use of ALL the cookies.')}</p>
                            <div className="flex flex-col md:flex-row justify-center gap-3 md:gap-6 w-full whitespace-nowrap">
                                <button onClick={handleSettingsClick} className="btn h-[56px] md:max-w-[344px] border border-[#808080] text-button-accent-color hover:text-dark-blu">{__t('Cookie settings')}</button>
                                <button onClick={handleAccept} className="btn h-[56px] md:max-w-[344px] bg-button-accent-color text-white hover:bg-dark-blu">{__t('Accept')}</button>
                            </div>
                        </div>
                    </Container>
                </div>
            </Transition>
        </>
    );
};

type SettingProps = {
    label: string;
    value?: boolean;
    onChange?: React.Dispatch<React.SetStateAction<boolean>>;
    description: string;
    isOpen: boolean;
    handleOpen: () => void;
    fixed?: true;
};

const Setting: React.FC<SettingProps> = ({
    label,
    value,
    onChange,
    description,
    isOpen,
    handleOpen,
    fixed
}) => {
    const [maxHeight, setMaxHeight] = useState<string>("0px");
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (contentRef.current && isOpen) {
            const scrollHeight = contentRef.current.scrollHeight;
            setMaxHeight(`${scrollHeight}px`);
        } else {
            setMaxHeight("0px");
        }
    }, [isOpen]);
    return (
        <div className="bg-white shadow-cookie w-full px-3 md:px-5 lg:px-6 rounded-xl overflow-visible">
            <div
                className={`flex items-center justify-between w-full cursor-pointer py-3 ${isOpen ? 'pt-[15px]' : ''} transition-all duration-300`}
            >
                <div
                    onClick={handleOpen}
                    className="flex items-center grow gap-2 md:gap-3"
                >
                    <svg className={`transition-transform transform duration-200 ${isOpen ? '-rotate-180' : 'rotate-0'}`} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M3 9L11.7805 17L21 9" stroke="#808080" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span className="pr-2 text-base-16 font-semibold lg:text-[18px] lg:leading-[26px] w-full">
                        {__t(label)}
                    </span>
                </div>
                {!!onChange && (
                    <label
                        onChange={() => onChange(!value)}
                        className="flex text-[15px] leading-[21px] text-[#2C2D33] items-center cursor-pointer"
                    >
                        {!fixed ? (
                            <>
                                <span className="mr-2">
                                    {value ? __t('Enabled') : __t('Disabled')}
                                </span>
                                <input
                                    className="sr-only peer cursor-pointer z-[100]"
                                    type="checkbox"
                                    checked={value}
                                />
                                <div className="toggle relative w-[60px] h-8 bg-stroke-color cursor-pointer rounded-full peer peer-checked:after:translate-x-[120%]
                                                    rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px]
                                                    after:start-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all
                                                    peer-checked:bg-button-accent-color"></div>
                            </>
                        ) : (
                            <span className="whitespace-nowrap">
                                {__t('Always Enabled')}
                            </span>
                        )}

                    </label>
                )}
            </div>
            <div
                ref={contentRef}
                style={{ maxHeight, transition: 'max-height 0.2s ease-in-out' }}
                className="overflow-hidden"
            >
                <p className="text-[15px] leading-[21px] md:text-base-16 text-[#6B6C70] pb-[15px]">{__t(description)}</p>
            </div>
        </div>
    );
};


export default Cookies;
